import { useMemo, type ReactNode } from "react";
import { CalendarOff, Building2, ExternalLink, Eye, MapPin, Pencil, Users, Video } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import type { Bloqueio, Reuniao } from "@/components/Agenda/WeekGrid";
import GoogleCalendarIcon from "@/components/Agenda/GoogleCalendarIcon";
import { DIAS_CURTOS, STATUS_REUNIAO, TIPO_LOCAL, ehHoje, hhmm, mesmoDia } from "@/components/Agenda/agenda-utils";

/**
 * Mês em grade, como o Google Agenda: semanas nas linhas, dias nas colunas.
 *
 * A pergunta que o mês responde é "como está a carga das próximas semanas",
 * não "que horas exatamente" — por isso cada dia é uma lista curta, dia
 * inteiro primeiro (chips cheios), depois os com hora em ordem ("14:00
 * Kickoff"), e o que não cabe vira "+N", que abre o dia.
 *
 * Clicar num item abre um RESUMO (popover), não a edição: é o gesto do
 * Google, e vale igual para reunião, bloqueio e compromisso do Google — o
 * que muda são as ações no rodapé (ver/editar aqui, ou abrir lá).
 */

type Props = {
  /** As semanas do mês, já completas de domingo a sábado. */
  dias: Date[];
  /** Mês de referência — dias de fora dele ficam apagados. */
  mes: Date;
  reunioes: Reuniao[];
  bloqueios: Bloqueio[];
  modoEquipe: boolean;
  onAbrirReuniao: (id: number) => void;
  onEditarReuniao?: (id: number) => void;
  onAbrirBloqueio?: (b: Bloqueio) => void;
  onEscolherDia: (d: Date) => void;
};

const MAX_POR_DIA = 4;

type Item =
  | { tipo: "reuniao"; ini: Date; r: Reuniao }
  | { tipo: "bloqueio"; ini: Date; diaInteiro: boolean; b: Bloqueio };

function cobreODia(ini: Date, fim: Date, dia: Date): boolean {
  const d0 = new Date(dia);
  d0.setHours(0, 0, 0, 0);
  const d1 = new Date(d0);
  d1.setDate(d1.getDate() + 1);
  return ini < d1 && fim > d0;
}

function dataLonga(d: Date | string): string {
  return new Date(d).toLocaleDateString("pt-BR", { weekday: "long", day: "numeric", month: "long" });
}

/** "terça-feira, 15 de setembro · 14:00 – 16:00" ou "15 – 16 de setembro · dia inteiro". */
function quando(ini: Date | string, fim: Date | string, diaInteiro: boolean): string {
  const a = new Date(ini);
  const b = new Date(fim);
  if (diaInteiro) {
    // `fim` é exclusivo: o último dia coberto é o anterior.
    const ultimo = new Date(b.getTime() - 1);
    return mesmoDia(a, ultimo)
      ? `${dataLonga(a)} · dia inteiro`
      : `${a.toLocaleDateString("pt-BR", { day: "numeric", month: "long" })} – ${ultimo.toLocaleDateString("pt-BR", { day: "numeric", month: "long" })} · dia inteiro`;
  }
  return mesmoDia(a, b)
    ? `${dataLonga(a)} · ${hhmm(a)} – ${hhmm(b)}`
    : `${dataLonga(a)} ${hhmm(a)} – ${dataLonga(b)} ${hhmm(b)}`;
}

export default function MonthGrid({
  dias,
  mes,
  reunioes,
  bloqueios,
  modoEquipe,
  onAbrirReuniao,
  onEditarReuniao,
  onAbrirBloqueio,
  onEscolherDia,
}: Props) {
  const porDia = useMemo(() => {
    return dias.map((dia) => {
      const itens: Item[] = [];
      for (const r of reunioes) {
        const ini = new Date(r.meeting.startAt);
        const fim = new Date(r.meeting.endAt);
        if (cobreODia(ini, fim, dia)) itens.push({ tipo: "reuniao", ini, r });
      }
      for (const b of bloqueios) {
        const ini = new Date(b.block.startAt);
        const fim = new Date(b.block.endAt);
        if (!cobreODia(ini, fim, dia)) continue;
        // Dia inteiro = flag, ou atravessa este dia inteiro (bloqueio de vários dias).
        const d0 = new Date(dia);
        d0.setHours(0, 0, 0, 0);
        const d1 = new Date(d0);
        d1.setDate(d1.getDate() + 1);
        const diaInteiro = b.block.allDay || (ini <= d0 && fim >= d1);
        itens.push({ tipo: "bloqueio", ini, diaInteiro, b });
      }
      // Dia inteiro primeiro; depois por hora.
      itens.sort((a, b) => {
        const ai = a.tipo === "bloqueio" && a.diaInteiro ? 0 : 1;
        const bi = b.tipo === "bloqueio" && b.diaInteiro ? 0 : 1;
        return ai - bi || a.ini.getTime() - b.ini.getTime();
      });
      return { dia, itens };
    });
  }, [dias, reunioes, bloqueios]);

  // Quantos dias por semana chegaram (7, ou 5 quando o fim de semana está
  // escondido). A ordem dos dias já vem certa; só o tamanho da linha muda.
  const diasDaSemana = useMemo(() => {
    const vistos: number[] = [];
    for (const d of dias) {
      if (vistos.includes(d.getDay())) break;
      vistos.push(d.getDay());
    }
    return vistos;
  }, [dias]);
  const colunas = diasDaSemana.length || 7;
  const semanas: typeof porDia[] = [];
  for (let i = 0; i < porDia.length; i += colunas) semanas.push(porDia.slice(i, i + colunas));
  const gridCols = { gridTemplateColumns: `repeat(${colunas}, minmax(0, 1fr))` };

  return (
    <div className="rounded-lg border border-border bg-card overflow-hidden">
      <div className="grid border-b border-border bg-muted/40" style={gridCols}>
        {diasDaSemana.map((d) => (
          <div key={d} className="px-2 py-1.5 text-[11px] uppercase tracking-wide text-muted-foreground text-center">
            {DIAS_CURTOS[d]}
          </div>
        ))}
      </div>
      {semanas.map((semana, si) => (
        <div key={si} className="grid border-b border-border last:border-b-0" style={{ minHeight: "7.5rem", ...gridCols }}>
          {semana.map(({ dia, itens }) => {
            const hoje = ehHoje(dia);
            const foraDoMes = dia.getMonth() !== mes.getMonth();
            const visiveis = itens.slice(0, MAX_POR_DIA);
            const ocultos = itens.length - visiveis.length;
            return (
              <div
                key={dia.toISOString()}
                className={`border-l border-border first:border-l-0 px-1 py-1 min-w-0 flex flex-col gap-0.5 ${
                  foraDoMes ? "bg-muted/30 text-muted-foreground" : ""
                }`}
                onDoubleClick={() => onEscolherDia(dia)}
              >
                <button
                  type="button"
                  onClick={() => onEscolherDia(dia)}
                  title="Abrir o dia"
                  className={`self-end text-xs font-semibold leading-none rounded-full h-6 w-6 inline-flex items-center justify-center hover:ring-2 hover:ring-primary/40 ${
                    hoje ? "bg-primary text-primary-foreground" : ""
                  }`}
                >
                  {dia.getDate()}
                </button>

                {visiveis.map((it) =>
                  it.tipo === "reuniao" ? (
                    <ItemReuniao
                      key={`r-${it.r.meeting.id}`}
                      r={it.r}
                      continua={!mesmoDia(it.ini, dia)}
                      modoEquipe={modoEquipe}
                      onVer={onAbrirReuniao}
                      onEditar={onEditarReuniao}
                    />
                  ) : (
                    <ItemBloqueio
                      key={`b-${it.b.block.id}`}
                      b={it.b}
                      diaInteiro={it.diaInteiro}
                      modoEquipe={modoEquipe}
                      onEditar={onAbrirBloqueio}
                    />
                  ),
                )}

                {ocultos > 0 && (
                  <button
                    type="button"
                    onClick={() => onEscolherDia(dia)}
                    className="text-left text-[11px] leading-4 px-1 text-muted-foreground hover:text-foreground"
                  >
                    +{ocultos} mais
                  </button>
                )}
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}

// ─── Resumo (popover) ────────────────────────────────────────────────────────

function Resumo({
  gatilho,
  titulo,
  quando: q,
  origem,
  linhas,
  acoes,
}: {
  gatilho: ReactNode;
  titulo: string;
  quando: string;
  origem: ReactNode;
  linhas: ReactNode[];
  acoes: ReactNode;
}) {
  return (
    <Popover>
      <PopoverTrigger asChild>{gatilho}</PopoverTrigger>
      <PopoverContent align="start" className="w-80 p-0 overflow-hidden" onOpenAutoFocus={(e) => e.preventDefault()}>
        <div className="p-3 space-y-2">
          <div className="flex items-start justify-between gap-2">
            <p className="font-semibold text-sm leading-snug">{titulo}</p>
            {origem}
          </div>
          <p className="text-xs text-muted-foreground first-letter:uppercase">{q}</p>
          {linhas.filter(Boolean).length > 0 && (
            <div className="space-y-1 pt-1 text-xs">{linhas}</div>
          )}
        </div>
        <div className="flex items-center justify-end gap-1.5 border-t bg-muted/30 px-2 py-1.5">{acoes}</div>
      </PopoverContent>
    </Popover>
  );
}

function ItemReuniao({
  r,
  continua,
  modoEquipe,
  onVer,
  onEditar,
}: {
  r: Reuniao;
  continua: boolean;
  modoEquipe: boolean;
  onVer: (id: number) => void;
  onEditar?: (id: number) => void;
}) {
  const status = STATUS_REUNIAO[r.meeting.status];
  const nomes = (r.attendees ?? []).map((a) => a.user?.name ?? a.user?.email).filter(Boolean).join(", ");
  const local = r.meeting.meetingUrl || r.meeting.locationText;
  return (
    <Resumo
      gatilho={
        <button
          type="button"
          title={`${hhmm(r.meeting.startAt)} – ${hhmm(r.meeting.endAt)} · ${r.meeting.title}`}
          className="w-full text-left text-[11px] leading-4 truncate rounded px-1 hover:bg-accent flex items-center gap-1"
        >
          <span className={`h-2 w-2 rounded-full shrink-0 border-2 ${(status?.barra ?? "border-l-sky-500").replace("border-l-", "border-")}`} />
          {!continua && <span className="text-muted-foreground">{hhmm(r.meeting.startAt)}</span>}
          <span className="truncate font-medium">{r.meeting.title}</span>
        </button>
      }
      titulo={r.meeting.title}
      quando={quando(r.meeting.startAt, r.meeting.endAt, false)}
      origem={status ? <Badge className={`${status.tint} border-0 shrink-0`}>{status.label}</Badge> : null}
      linhas={[
        r.contact?.name && (
          <p key="c" className="flex items-center gap-1.5"><Building2 className="h-3.5 w-3.5 text-muted-foreground shrink-0" />{r.contact.name}</p>
        ),
        nomes && (
          <p key="p" className="flex items-center gap-1.5"><Users className="h-3.5 w-3.5 text-muted-foreground shrink-0" /><span className="truncate">{nomes}</span></p>
        ),
        local && (
          <p key="l" className="flex items-center gap-1.5">
            {r.meeting.meetingUrl ? <Video className="h-3.5 w-3.5 text-muted-foreground shrink-0" /> : <MapPin className="h-3.5 w-3.5 text-muted-foreground shrink-0" />}
            <span className="truncate">{TIPO_LOCAL[r.meeting.locationType ?? ""] ? `${TIPO_LOCAL[r.meeting.locationType ?? ""]} · ` : ""}{local}</span>
          </p>
        ),
      ]}
      acoes={
        <>
          <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => onVer(r.meeting.id)}>
            <Eye className="h-3.5 w-3.5 mr-1" /> Ver detalhes
          </Button>
          {onEditar && (
            <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => onEditar(r.meeting.id)}>
              <Pencil className="h-3.5 w-3.5 mr-1" /> Editar
            </Button>
          )}
        </>
      }
    />
  );
}

function ItemBloqueio({
  b,
  diaInteiro,
  modoEquipe,
  onEditar,
}: {
  b: Bloqueio;
  diaInteiro: boolean;
  modoEquipe: boolean;
  onEditar?: (b: Bloqueio) => void;
}) {
  const google = b.block.externalProvider === "google";
  const espelho = b.block.sourceMeetingId != null;
  const livre = b.block.busy === false;
  const quem = modoEquipe && b.user ? `${b.user.name?.split(" ")[0] ?? ""} · ` : "";
  const titulo = b.block.reason || (google ? "Compromisso" : "Agenda bloqueada");
  const texto = `${quem}${titulo}`;
  const editavel = !!onEditar && !google && !espelho;

  const gatilho = diaInteiro ? (
    <button
      type="button"
      title={texto}
      className={`w-full text-left text-[11px] leading-4 truncate rounded px-1 flex items-center gap-1 border-l-[3px] font-medium ${
        google
          ? livre
            ? "border-l-amber-300 bg-amber-50/80 text-amber-800 dark:bg-amber-950/25 dark:text-amber-200"
            : "border-l-amber-400 bg-amber-100/90 text-amber-900 dark:bg-amber-950/50 dark:text-amber-100"
          : "border-l-rose-700 bg-rose-200/60 text-rose-950 dark:bg-rose-950/60 dark:text-rose-100"
      } hover:brightness-95`}
    >
      {google ? <GoogleCalendarIcon className="h-3 w-3" /> : <CalendarOff className="h-2.5 w-2.5 shrink-0" />}
      <span className="truncate">{texto}</span>
    </button>
  ) : (
    <button
      type="button"
      title={`${hhmm(b.block.startAt)} – ${hhmm(b.block.endAt)} · ${texto}`}
      className={`w-full text-left text-[11px] leading-4 truncate rounded px-1 flex items-center gap-1 ${
        google ? "text-amber-900 dark:text-amber-200 hover:bg-amber-500/10" : "text-rose-900 dark:text-rose-200 hover:bg-rose-500/10"
      }`}
    >
      {google ? <GoogleCalendarIcon className="h-3 w-3" /> : <CalendarOff className="h-2.5 w-2.5 shrink-0" />}
      <span className="text-muted-foreground">{hhmm(b.block.startAt)}</span>
      <span className="truncate font-medium">{texto}</span>
    </button>
  );

  return (
    <Resumo
      gatilho={gatilho}
      titulo={titulo}
      quando={quando(b.block.startAt, b.block.endAt, b.block.allDay || diaInteiro)}
      origem={
        google ? (
          <Badge variant="outline" className="gap-1 shrink-0 font-normal"><GoogleCalendarIcon className="h-3 w-3" /> Google Agenda</Badge>
        ) : espelho ? (
          <Badge variant="outline" className="gap-1 shrink-0 font-normal"><Building2 className="h-3 w-3" /> outra empresa</Badge>
        ) : (
          <Badge variant="outline" className="gap-1 shrink-0 font-normal"><CalendarOff className="h-3 w-3" /> Bloqueio</Badge>
        )
      }
      linhas={[
        b.user?.name && (
          <p key="u" className="flex items-center gap-1.5"><Users className="h-3.5 w-3.5 text-muted-foreground shrink-0" />{b.user.name}</p>
        ),
        google && (
          <p key="g" className="text-muted-foreground">
            {livre ? "Marcado como “Disponível” no Google: aparece aqui, mas não ocupa a agenda." : "Ocupa a agenda: entra nos conflitos e nos horários livres."}
          </p>
        ),
        espelho && (
          <p key="e" className="text-muted-foreground">Vem de uma reunião marcada em outra empresa; some sozinho se ela for cancelada.</p>
        ),
      ]}
      acoes={
        google ? (
          b.block.externalUrl ? (
            <Button size="sm" variant="outline" className="h-7 text-xs" asChild>
              <a href={b.block.externalUrl} target="_blank" rel="noreferrer">
                <ExternalLink className="h-3.5 w-3.5 mr-1" /> Abrir no Google Agenda
              </a>
            </Button>
          ) : (
            <span className="text-[11px] text-muted-foreground px-1">Edite no Google Agenda</span>
          )
        ) : editavel ? (
          <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => onEditar?.(b)}>
            <Pencil className="h-3.5 w-3.5 mr-1" /> Editar
          </Button>
        ) : (
          <span className="text-[11px] text-muted-foreground px-1">{espelho ? "Cancele ou remarque a reunião na outra empresa" : "Somente leitura"}</span>
        )
      }
    />
  );
}
